(() => {
  'use strict';
  const copyData = (target, source) => {
    for (const attribute of [...target.attributes]) {
      if (attribute.name.startsWith('data-')) target.removeAttribute(attribute.name);
    }
    for (const attribute of [...source.attributes]) {
      if (attribute.name.startsWith('data-')) target.setAttribute(attribute.name, attribute.value);
    }
  };
  const incomingProduct = (html, selector, productId) =>
    [...(html?.querySelectorAll(selector) || [])].find(node => node.dataset.productId === productId);
  const cartRequests = new WeakMap();
  const currentCart = event => {
    if (!cartRequests.has(event)) {
      const root = window.Shopify?.routes?.root || '/';
      cartRequests.set(event, fetch(root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(response => { if (!response.ok) throw new Error('Cart unavailable'); return response.json(); }));
    }
    return cartRequests.get(event);
  };

  class GgDelivery extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.section = this.closest('.shopify-section');
      this.picker = this.section?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
      this.pending = false;
      if (this.dataset.cart !== 'true') {
        this.section?.addEventListener('variant:selected', event => {
          const picker = event.target.closest('variant-picker');
          if (picker?.dataset.productId !== this.dataset.productId) return;
          this.pending = true;
          this.refresh();
        }, options);
        this.section?.addEventListener('variant:update', event => {
          const data = event.detail?.data;
          if (String(data?.productId) !== this.dataset.productId && !(data?.newProduct && event.target === this.picker)) return;
          const incoming = incomingProduct(data.html, 'gg28-delivery', String(data.newProduct?.id || this.dataset.productId));
          if (incoming) copyData(this, incoming);
          else this.dataset.eligible = 'false';
          this.pending = false;
          queueMicrotask(() => this.isConnected && this.refresh());
        }, options);
        this.section?.addEventListener('input', () => this.refresh(), options);
        this.section?.addEventListener('quantity-selector:update', () => this.refresh(), options);
      }
      document.addEventListener('cart:update', event => this.updateCart(event), options);
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this.refresh();
      }, options);
      this.observer = new MutationObserver(() => this.refresh());
      this.observer.observe(this, { attributes: true, attributeFilter: ['data-eligible', 'data-country', 'data-stock', 'data-variant-id'] });
      this.tick();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      window.clearTimeout(this.timer);
    }

    tick() {
      this.refresh();
      this.timer = window.setTimeout(() => this.tick(), 60000 - Date.now() % 60000 + 25);
    }

    updateCart(event) {
      if (this.dataset.cart === 'true') {
        const sections = event.detail?.data?.sections || {};
        let incoming;
        for (const markup of Object.values(sections)) {
          if (typeof markup !== 'string') continue;
          incoming = new DOMParser().parseFromString(markup, 'text/html').querySelector('gg28-delivery[data-cart="true"]');
          if (incoming) break;
        }
        if (incoming) {
          copyData(this, incoming);
          this.innerHTML = incoming.innerHTML;
        } else {
          this.dataset.eligible = 'false';
          this.dataset.fallback = this.dataset.sourcing;
        }
      } else {
        this.cartPending = true;
        const request = this.cartRequest = event;
        currentCart(event).then(cart => {
          if (!this.isConnected || this.cartRequest !== request || !Array.isArray(cart.items)) return;
          this.dataset.cartQuantity = String(cart.items.filter(item => String(item.variant_id || item.id) === this.dataset.variantId)
            .reduce((total, item) => total + Number(item.quantity || 0), 0));
          this.cartPending = false;
          this.refresh();
        }).catch(() => {
          if (!this.isConnected || this.cartRequest !== request) return;
          this.cartPending = true;
          this.refresh();
        });
      }
      queueMicrotask(() => this.isConnected && this.refresh());
    }

    refresh() {
      const estimate = this.querySelector('[data-gg28-estimate]');
      const fallback = this.querySelector('[data-gg28-fallback]');
      if (!estimate || !fallback) return;
      let eligible = this.dataset.eligible === 'true' && !this.pending && !this.cartPending;
      let overstock = false;
      if (this.dataset.cart !== 'true' && this.dataset.managed === 'true') {
        const input = this.section?.querySelector(`quantity-selector-component[data-variant-id="${this.dataset.variantId}"] input[name="quantity"]`);
        const requested = Number(input?.value || 1);
        const inCart = Number(this.dataset.cartQuantity || 0);
        overstock = !Number.isFinite(requested) || requested < 1 || requested + inCart > Number(this.dataset.stock);
        eligible = eligible && !overstock;
      }
      let result;
      try {
        result = window.GrindDelivery?.calculate(new Date(), {
          ...(window.GG28DeliveryConfig || {}),
          countryCode: this.dataset.country,
          available: eligible,
          fulfilment: 'stocked'
        });
      } catch (_) {
        result = null;
      }
      const ready = !!result?.eligible;
      estimate.hidden = !ready;
      fallback.hidden = ready;
      this.dataset.state = ready ? 'ready' : this.pending ? 'updating' : 'fallback';
      fallback.textContent = this.pending ? this.dataset.updating : overstock || this.cartPending ? this.dataset.sourcing : this.dataset.fallback;
      if (!ready) {
        this.querySelector('[data-gg28-range]').textContent = '';
        return;
      }
      const start = new Date(result.deliveryStart + 'T00:00:00Z');
      const end = new Date(result.deliveryEnd + 'T00:00:00Z');
      const localYear = Number(result.orderDate.slice(0, 4));
      const language = document.documentElement.lang || 'en-GB';
      const locale = language.toLowerCase() === 'en' ? 'en-GB' : language;
      const dateOptions = { timeZone: 'UTC', day: 'numeric', month: 'long' };
      if (start.getUTCFullYear() !== localYear || end.getUTCFullYear() !== localYear) dateOptions.year = 'numeric';
      const formatter = new Intl.DateTimeFormat(locale, dateOptions);
      this.querySelector('[data-gg28-range]').textContent = formatter.formatRange ? formatter.formatRange(start, end) : formatter.format(start) + ' – ' + formatter.format(end);
      this.querySelector('[data-gg28-cutoff]').textContent = result.afterCutoff ? this.dataset.after : this.dataset.before;
      const dispatch = this.querySelector('[data-gg28-dispatch]');
      dispatch.dateTime = result.dispatchDate;
      dispatch.textContent = new Intl.DateTimeFormat(locale, { ...dateOptions, weekday: 'long' }).format(new Date(result.dispatchDate + 'T00:00:00Z'));
    }
  }

  class GgPurchase extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const section = this.closest('.shopify-section');
      this.picker = section?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
      section?.addEventListener('variant:update', event => {
        const data = event.detail?.data;
        if (String(data?.productId) !== this.dataset.productId && !(data?.newProduct && event.target === this.picker)) return;
        const incoming = incomingProduct(data.html, 'gg27-purchase', String(data.newProduct?.id || this.dataset.productId));
        if (incoming) {
          this.dataset.productId = incoming.dataset.productId;
          this.innerHTML = incoming.innerHTML;
        }
      }, { signal: this.controller.signal });
    }
    disconnectedCallback() { this.controller?.abort(); }
  }

  class GgHelpDialog extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      document.addEventListener('click', event => {
        const trigger = event.target.closest('[data-gg28-help]');
        if (!trigger || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.opener = trigger;
        if (!this.dialog.open) this.dialog.showModal();
        this.dialog.querySelector('[data-gg28-close]')?.focus();
      }, options);
      this.querySelector('[data-gg28-close]')?.addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      }, options);
      this.dialog.addEventListener('close', () => {
        if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      }, options);
    }
    disconnectedCallback() {
      this.controller?.abort();
      if (this.dialog?.open) this.dialog.close();
    }
  }

  if (!customElements.get('gg28-delivery')) customElements.define('gg28-delivery', GgDelivery);
  if (!customElements.get('gg27-purchase')) customElements.define('gg27-purchase', GgPurchase);
  if (!customElements.get('gg28-help-dialog')) customElements.define('gg28-help-dialog', GgHelpDialog);
})();
